/**
 * iter101.4 — Impersonation session pill.
 * iter101.47 — Redesigned to a compact chip that sits alongside every
 *   portal header (doctor, patient, coe, admin) so it's discoverable
 *   without covering the workspace. The chip shows a small warning
 *   glyph + the impersonated identity, and exposes a single X icon to
 *   end the session.
 * iter102.13 — Moved from TOP-RIGHT to TOP-CENTER so it no longer
 *   overlaps the Logout button / user-menu that most portal headers
 *   already anchor on the right edge.
 *
 * Renders a compact chip in the top-center of the viewport ONLY inside
 * impersonated tabs (i.e. tabs where `sessionStorage.ca_imp_active`
 * is set — the /impersonate-land landing page sets this flag right
 * before navigating to the target portal).
 *
 * Purpose: Make it crystal-clear which identity the operator is viewing
 * so nobody accidentally treats a support-side impersonation as a real
 * user session. Also gives them a one-click X escape hatch.
 *
 * Design notes:
 *   • Reads directly from sessionStorage — no API round-trip. The label
 *     was stashed there by /impersonate-land during the handshake.
 *   • Universal: renders inside every portal (doctor, patient, coe,
 *     admin) so the visual grammar is identical regardless of which
 *     staff role is impersonating whom.
 *   • Compact — an amber pill horizontally centered at the top edge.
 *     Hover reveals the target-name tooltip; the visible label stays
 *     terse so it doesn't collide with page headers.
 *   • "X" is safe: sessionStorage is per-tab, so closing the tab
 *     automatically ends the impersonated session. No server round-
 *     trip needed. If window.close() is blocked (Chrome only allows
 *     it on tabs opened via script), we bounce the tab back to the
 *     admin console (the admin's own tab still has its localStorage
 *     token so this will land signed in).
 */
import React, { useEffect, useState } from "react";
import { X, Warning } from "@phosphor-icons/react";

const ROLE_COPY = {
    doctor: "Doctor portal",
    patient: "Patient portal",
    staff: "CoE portal",
    admin: "Admin portal",
};

export default function ImpersonationSessionPill() {
    const [visible, setVisible] = useState(false);
    const [label, setLabel] = useState("");
    const [role, setRole] = useState("");

    useEffect(() => {
        try {
            const active = sessionStorage.getItem("ca_imp_active") === "1";
            const lbl = sessionStorage.getItem("ca_imp_label") || "";
            const r = sessionStorage.getItem("ca_imp_role") || "";
            if (active && lbl) {
                setLabel(lbl);
                setRole(r);
                setVisible(true);
            }
        } catch { /* private browsing / SSR */ }
    }, []);

    if (!visible) return null;

    const roleCopy = ROLE_COPY[role] || "portal";

    const onClose = () => {
        try {
            sessionStorage.removeItem("ca_imp_active");
            sessionStorage.removeItem("ca_imp_label");
            sessionStorage.removeItem("ca_imp_role");
            sessionStorage.removeItem("ca_token");
        } catch { /* noop */ }
        try {
            window.close();
        } catch { /* noop */ }
        // Fallback when the tab wasn't opened via script (Chrome blocks
        // window.close in that case).
        setTimeout(() => {
            if (!window.closed) {
                setVisible(false);
                window.location.replace("/admin");
            }
        }, 250);
    };

    return (
        <div
            className="fixed top-3 left-1/2 -translate-x-1/2 sm:top-4 z-[10000] pointer-events-none"
            data-testid="impersonation-session-pill"
        >
            <div
                className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50/95 shadow-md shadow-amber-200/40 backdrop-blur px-2 py-1 text-[11px] max-w-[min(90vw,320px)]"
                title={`Impersonating ${label} · ${roleCopy}`}
            >
                <Warning size={11} weight="fill" className="text-amber-700 shrink-0" />
                <span className="text-[9px] uppercase tracking-widest font-bold text-amber-700 shrink-0 hidden sm:inline">
                    Impersonating
                </span>
                <span className="text-amber-900 font-semibold truncate">
                    {label}
                </span>
                <button
                    type="button"
                    onClick={onClose}
                    className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-700 hover:bg-amber-800 text-white transition-colors shrink-0"
                    data-testid="impersonation-session-close"
                    aria-label="End impersonation and close tab"
                    title="End impersonation"
                >
                    <X size={10} weight="bold" />
                </button>
            </div>
        </div>
    );
}
